import React, {useContext, useMemo, useRef, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import {FaClipboardList, FaPen, FaPlus, FaTrash} from "react-icons/fa";
import Button from "../../../../../../components/button/button.jsx";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import {v4} from "uuid";
import projectUtils from "../../../../../../utils/project-utils.js";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import citizenAlertMessages from "../../utils/citizen-alert-messages.js";
import {toast} from "react-toastify";
import citizenService from "../../../../../../services/web/citizen-service.js";
import occurrenceService from "../../../../../../services/web/occurrence-service.js";
import useHasPermission from "../../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../../utils/enums/permission-enum.js";
import {CitizenContext} from "../context/citizen-context.jsx";
import InputFile from "../../../../../../components/input/input-file.jsx";
import readImageFile from "../utils/read-image-file.js";

const CitizenGeneralVehicles = ({vehicles, onChange, title, height = "h-[300px]"}) => {
    const {attaches, setAttaches} = useContext(CitizenContext);

    const [open, setOpen] = useState(false);
    const [plate, setPlate] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(undefined);
    const [targetVehicle, setTargetVehicle] = useState(undefined);

    const gridRef = useRef(undefined);
    const attachesRef = useRef(attaches);

    attachesRef.current = attaches;

    const hasPermission = useHasPermission();
    const occurrencePermission = hasPermission(PermissionEnum.OCCURRENCE);

    const addVehicle = () => {
        setPlate("");
        setDescription("");
        setFile(undefined);
        setOpen(true);
        setTargetVehicle(undefined)
    };

    const searchOccurrencesByPlate = async () => {
        const occurrences = await occurrenceService.findByPlate(plate.replace("-", ""));
        return occurrences?.map(item => item.id) ?? [];
    }

    const buildFilePreview = (attachId) => {
        const attach = attachId ? attachesRef.current.find((a) => a.attachId === attachId) : undefined;

        if (!attach) return undefined;

        return {
            attachId,
            name: attach.name,
            type: attach.type,
            extension: attach.extension ?? attach.type,
            url: attach.url,
            file: attach.new ? attach : undefined
        };
    }

    const syncAttaches = (previousAttachId, currentAttachId) => {
        if (previousAttachId === currentAttachId) return;

        setAttaches(prev => {
            const remaining = prev.filter((i) => i.attachId !== previousAttachId && i.attachId !== currentAttachId);
            return file?.file ? [...remaining, file.file] : remaining;
        });
    }

    const attachVehicleImage = (data) => {
        readImageFile([data.file], (raw) => setFile({...data, attachId: raw.attachId}));
    }

    const removeFile = () => setFile(undefined);

    const upsertVehicle = async (newVehicle) => {
        if (!plate) {
            projectUtils.swalErrorMessage("", citizenAlertMessages["missing.vehicle.plate"], "Ok");
            return;
        }

        const occurrences = await searchOccurrencesByPlate();

        const attachId = file?.attachId ?? null;

        const updated = newVehicle ?
            [...vehicles, {id: v4(), plate: plate.replace("-", ""), description, attachId, occurrences}] :
            vehicles.map((v) => v.id === targetVehicle ? {...v, plate: plate.replace("-", ""), description, attachId} : v);

        syncAttaches(vehicles.find((v) => v.id === targetVehicle)?.attachId ?? null, attachId);

        onChange(updated);
        setOpen(false);
    }

    const removeVehicleRef = useRef(undefined);

    removeVehicleRef.current = (vehicle) => {
        const updated = vehicles.filter((i) => i.id !== vehicle.id);
        setAttaches(prev => prev.filter((i) => i.attachId !== vehicle.attachId));
        onChange(updated);
    }

    const handleData = (data) => {
        setPlate(data?.plate);
        setDescription(data?.description);
        setTargetVehicle(data?.id);
        setFile(buildFilePreview(data?.attachId));
    }

    const [columnDefs,] = useState([
        { field: "plate", headerName: "Placa", flex: 1, valueFormatter: ({value}) => projectUtils.formatPlate(value)},
        { field: "description", headerName: "Descrição", flex: 2 },
        {
            headerName: "",
            field: "",
            width: occurrencePermission ? 150 : 100,
            cellRenderer: ({data, setData, setOpen, remove, noEdit = true}) => {
                const edit = () => {
                    setOpen(true)
                    setData(data)
                }

                const createOccurrence = () => {
                    const plate = data.plate;
                    if (!plate || plate.length < 7) {
                        toast.error("Insira uma placa válida.")
                        return;
                    }

                    citizenService.createOccurrence(plate)
                        .then(() => {
                            toast.success("O cadastro de ocorrência será aberto no SentryView.")
                        })
                        .catch(error => {
                            console.error(error);
                            toast.error("Ocorreu um erro ao abrir o cadastro no SentryView.")
                        });
                }

                const viewOccurrences = () => {
                    const formData = new FormData();
                    formData.append("occurrences", JSON.stringify(data?.occurrences))

                    occurrenceService.openSentryViewOccurrencesList(formData)
                        .then(() => {
                            toast.success("A lista de ocorrências associadas será aberta no SentryView.")
                        })
                        .catch(error => {
                            console.error(error);
                            toast.error("Ocorreu um erro ao abrir as ocorrências no SentryView.")
                        });
                }

                return (
                    <div className="flex gap-1 justify-center items-center h-full w-full">
                        {
                            occurrencePermission ? data?.occurrences?.length ?
                            <Button target="primary" className={"w-7.5! h-6.25! p-0!"} title={"Visualizar ocorrências"} onClick={viewOccurrences}>
                                <FaClipboardList />
                            </Button> :
                            <Button target="default" className={"w-7.5! h-6.25! p-0!"} title={"Criar ocorrência"} onClick={createOccurrence}>
                                <FaPlus />
                            </Button> : ""
                        }
                        {
                            noEdit &&
                            <Button target="primary" className={"w-7.5! h-6.25! p-0!"} onClick={edit} title={"Editar"}>
                                <FaPen />
                            </Button>
                        }
                        <Button target="danger" className={"w-7.5! h-6.25! p-0!"} onClick={() => remove(data)} title={"Remover"}>
                            <FaTrash />
                        </Button>
                    </div>
                );
            },
            cellRendererParams: {setData: handleData, setOpen, remove: (vehicle) => removeVehicleRef.current(vehicle)}
        },
    ]);

    const defaultColDef = useMemo(() => ({
        comparator: aggridUtils.nullLastComparator
    }), []);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>{title}</Card.Title>

                    <Button onClick={addVehicle}>
                        <FaPlus />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className={"ag-theme-quartz grid " + height}>
                        <AgGridReact
                            ref={gridRef}
                            rowHeight={35}
                            rowData={vehicles}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            paginationAutoPageSize={true}
                            pagination={true}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={theme}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal open={open} setOpen={setOpen} title={title} className={"w-2xl"}>
                <Modal.Body className={"!mt-0 p-4 grid grid-cols-12 gap-4"}>
                    <InputForm
                        id={"plate"}
                        value={plate}
                        mask={MaskDefault.plate}
                        label={"Placa *"}
                        classNameWrapper={"col-span-4"}
                        onChange={({target}) => setPlate(target.value?.toUpperCase())}
                    />
                    <InputForm
                        id={"description"}
                        value={description}
                        label={"Descrição"}
                        classNameWrapper={"col-span-8"}
                        onChange={({target}) => setDescription(target.value)}
                    />

                    <InputFile
                        className={"col-span-4"}
                        file={file}
                        setFile={attachVehicleImage}
                        removeFile={removeFile}
                        title={"Anexar foto"}
                        titleWithFile={"Visualizar foto"}
                        accept={"image/png, image/jpeg"}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button target={"primary"} onClick={() => upsertVehicle(!targetVehicle)}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CitizenGeneralVehicles;